'use client'

import SHA256 from 'crypto-js/sha256'
import dayjs from 'dayjs'

import { Session } from '@rewardops/auth'
import { PublicTenant } from '@rewardops/env'
import { isServer } from '@rewardops/utils'

import { isAirCanada } from '~/utils/public-tenant-utils'
import AirCanadaEventCache from '~/custom/air-canada/air-canada-event-cache'
import { AcEventParams } from '../air-canada-types'
import { getScreenSize, pushAcEvent } from '../air-canada-utils'

interface FireAirCanadaMainHeaderEventProps {
  tenant: PublicTenant
  session: Session
  build?: { date: string }
  language: string
}

const fireAirCanadaMainHeaderEvent = ({
  tenant,
  session,
  build,
  language,
}: FireAirCanadaMainHeaderEventProps) => {
  const eventKey = `mainheader`

  if (
    !isAirCanada(tenant) ||
    AirCanadaEventCache.hasEvent(eventKey) ||
    isServer()
  ) {
    return
  }

  AirCanadaEventCache.addEvent(eventKey)

  const { member } = session?.user ?? { member: null }
  const address = member?.address
  const buildDate = build?.date ?? 'unknown'

  const event: AcEventParams = {
    extraArgs: {
      siteInfo: {
        buildDate:
          buildDate === 'unknown'
            ? null
            : dayjs(buildDate).format('YYYY-MM-DD'),
        environment: 'aeroplan_apple_estore',
        language,
        name: 'aeroplan apple estore',
        siteEdition: 'ca',
        type: 'responsive',
        viewportSize: getScreenSize(),
        webProperty: 'aircanada.com',
      },
      userInfo: {
        authState: session ? 'logged-in' : 'guest',
        aco: {
          birthMonth: 0,
          birthYear: 0,
          category: null,
          userHasStatus: null,
          countryOfResidence: address?.countryCode.toLowerCase() ?? null,
          emailID: SHA256(member?.email?.toLowerCase() ?? 'N/A').toString(),
          loginMethod: 'aeroplan',
          postalCode: address?.postalCode.slice(0, 3).toLowerCase() ?? null,
          seatPreference: null,
          userID: session?.user?.member.id ?? null,
        },
      },
      profileInfo: {
        pointsBalance: member?.points ?? 0,
      },
    },
  }

  pushAcEvent(event, false)
}

interface FireAirCanadaLocaleChangeEventProps {
  tenant: PublicTenant
  language: string
}

const fireAirCanadaLocaleChangeEvent = ({
  tenant,
  language,
}: FireAirCanadaLocaleChangeEventProps) => {
  const eventKey = `locale-${language}`

  if (
    !isAirCanada(tenant) ||
    AirCanadaEventCache.hasEvent(eventKey) ||
    isServer()
  ) {
    return
  }

  AirCanadaEventCache.addEvent(eventKey)

  const event: AcEventParams = {
    extraArgs: {
      siteInfo: {
        language,
      },
    },
  }

  pushAcEvent(event, false)
}

export { fireAirCanadaMainHeaderEvent, fireAirCanadaLocaleChangeEvent }
